import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";
import { MapPin, Users, Star, Calendar as CalendarIcon, Clock, Camera } from "lucide-react";
import { format } from "date-fns";

const VenueDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState("");
  const [duration, setDuration] = useState(90);
  const [availability, setAvailability] = useState({ availableSlots: [], bookedSlots: [] });
  const [creating, setCreating] = useState(false);
  const [storyCount, setStoryCount] = useState(0);

  useEffect(() => {
    fetchVenue();
    fetchStoryCount();
  }, [id]);

  useEffect(() => {
    if (date) {
      fetchAvailability();
    }
  }, [date]);

  const fetchVenue = async () => {
    try {
      const response = await axios.get(`${API}/venues/${id}`);
      setVenue(response.data);
    } catch (error) {
      console.error("Error fetching venue:", error);
      toast.error("Saha bilgileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const fetchStoryCount = async () => {
    try {
      const response = await axios.get(`${API}/stories/venue/${id}`);
      setStoryCount(response.data?.totalStories || 0);
    } catch (error) {
      console.error("Error fetching story count:", error);
    }
  };

  const fetchAvailability = async () => {
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.get(`${API}/venues/${id}/availability?date=${dateStr}`);
      setAvailability(response.data);
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  const handleCreateMatch = async () => {
    if (!selectedTime) {
      toast.error("Lütfen bir saat seçin");
      return;
    }

    setCreating(true);
    try {
      const dateStr = format(date, "yyyy-MM-dd");
      const response = await axios.post(`${API}/matches`, {
        venueId: id,
        date: dateStr,
        time: selectedTime,
        duration: parseInt(duration),
        maxPlayers: 22
      });
      toast.success("Maç oluşturuldu ve rezervasyon yapıldı!");
      navigate(`/matches/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Maç oluşturulamadı");
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[80vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="container mx-auto px-4 py-8">
          <Card className="p-12 text-center">
            <p className="text-gray-500 text-lg">Saha bulunamadı</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300" data-testid="venue-detail">
      <Navbar user={user} onLogout={onLogout} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Venue Header */}
        <div className="mb-8">
          <div className="h-64 md:h-96 rounded-3xl overflow-hidden mb-6 relative">
            {venue.images && venue.images.length > 0 ? (
              <img 
                src={venue.images[0]} 
                alt={venue.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center">
                <CalendarIcon className="h-32 w-32 text-white opacity-50" />
              </div>
            )}
            <div className="absolute top-6 right-6">
              <Badge className="bg-white text-emerald-600 shadow-lg text-lg px-4 py-2">
                <Star className="h-4 w-4 mr-1 fill-current" />
                {venue.rating}
              </Badge>
            </div>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{venue.name}</h1>
          
          {/* Story Badge */}
          {storyCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/venue/${id}/stories`)}
              className="mb-4 bg-emerald-50 border-emerald-200 hover:bg-emerald-100"
            >
              <Camera className="h-4 w-4 mr-2 text-emerald-600" />
              <span className="text-emerald-700">{storyCount} Hikaye</span>
            </Button>
          )}
          
          <div className="flex flex-wrap gap-4 items-center text-gray-600">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-emerald-600" />
              <span className="text-lg">{venue.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-emerald-600" />
              <span className="text-lg">Kapasite: {venue.capacity} kişi</span>
            </div>
            <div className="text-3xl font-bold text-emerald-600">{venue.pricePerHour} TL/saat</div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Amenities */}
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle>Olanaklar</CardTitle>
            </CardHeader>
            <CardContent>
              {venue.amenities && venue.amenities.length > 0 ? (
                <div className="space-y-2">
                  {venue.amenities.map((amenity, idx) => (
                    <Badge key={idx} variant="outline" className="mr-2 mb-2">
                      {amenity}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">Bilgi yok</p>
              )}
            </CardContent>
          </Card>

          {/* Booking Form */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle>Maç Oluştur ve Rezervasyon Yap</CardTitle>
              <CardDescription>Bir tarih ve saat seçerek maç oluştur</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Tarih Seç</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal mt-2"
                          data-testid="date-picker-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(date, "dd MMMM yyyy")}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label htmlFor="duration">Süre (dakika)</Label>
                    <Select value={duration.toString()} onValueChange={(val) => setDuration(parseInt(val))}>
                      <SelectTrigger className="mt-2" data-testid="duration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="60">60 dakika</SelectItem>
                        <SelectItem value="90">90 dakika</SelectItem>
                        <SelectItem value="120">120 dakika</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label>Müsait Saatler</Label>
                  <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                    {availability.availableSlots.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Bu tarihte müsait saat yok</p>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {availability.availableSlots.map((slot) => (
                          <Button
                            key={slot}
                            variant={selectedTime === slot ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSelectedTime(slot)}
                            className={selectedTime === slot ? "bg-emerald-600" : ""}
                            data-testid={`time-slot-${slot}`}
                          >
                            {slot}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-emerald-50 rounded-lg">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-gray-600">Süre:</span>
                  <span className="font-semibold">{duration} dakika</span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-gray-600">Toplam Tutar:</span>
                  <span className="text-2xl font-bold text-emerald-600">
                    {(venue.pricePerHour * (duration / 60)).toFixed(2)} TL
                  </span>
                </div>
                <Button 
                  onClick={handleCreateMatch}
                  disabled={creating || !selectedTime}
                  className="w-full bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
                  data-testid="create-match-btn"
                >
                  {creating ? "Oluşturuluyor..." : "Maç Oluştur ve Rezerve Et"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default VenueDetail;